<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1100" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" hide-details />
        </div>

        <SectionLayout :settings="fields.settings" :container-page="fields.containerPage" />

        <div class="mb-5">
          <label class="label-input">Layout</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="4">
                <v-checkbox v-model="fields.layout.centerY" label="Center Y" hide-details class="mt-0" />
              </v-col>
              <v-col cols="12" md="4">
                <label>Gap Item (px)</label>
                <v-text-field v-model.number="fields.layout.gapItem" type="number" hide-details class="mt-0" />
              </v-col>
              <v-col cols="12" md="4">
                <label>Content Position</label>
                <v-select
                  v-model.number="fields.layout.contentOrder"
                  :items="[
                    { text: 'Left', value: 1 },
                    { text: 'Right', value: 2 },
                  ]"
                  hide-details
                  class="mt-0"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Rating</label>
          <div class="group-item">
            <v-checkbox v-model="fields.rateCard.show" label="Show rating" hide-details class="mt-0 mb-2" />
            <template v-if="fields.rateCard.show">
              <v-row>
                <v-col cols="12" md="4">
                  <label>Stars (0-5)</label>
                  <v-select
                    v-model.number="fields.rateCard.stars"
                    :items="[0, 1, 2, 3, 4, 5]"
                    hide-details
                    class="mt-0"
                  />
                </v-col>
                <v-col cols="12" md="8">
                  <label>Rating Text</label>
                  <v-text-field v-model="fields.rateCard.text" hide-details class="mt-0" />
                </v-col>
              </v-row>
            </template>
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Title</label>
          <div class="group-item">
            <v-checkbox v-model="fields.titleBlock.show" label="Show title" hide-details class="mt-0 mb-2" />
            <ckeditor
              v-if="fields.titleBlock.show"
              :editor="editor"
              v-model="fields.titleBlock.content"
              :config="editorConfig"
            />
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Policy Items</label>
          <div class="group-item mb-3">
            <v-checkbox v-model="fields.policyItems.show" label="Show policy items" hide-details class="mt-0 mb-2" />
            <template v-if="fields.policyItems.show">
              <v-row>
                <v-col cols="12" md="6">
                  <ColorPicker
                    :value="fields.policyItems.color"
                    name="policy-item-color"
                    label="Text Color"
                    default-color="#006196"
                    @input="value => (fields.policyItems.color = value)"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <ColorPicker
                    :value="fields.policyItems.background"
                    name="policy-item-background"
                    label="Background Color"
                    default-color="#F2F7F9"
                    @input="value => (fields.policyItems.background = value)"
                  />
                </v-col>
              </v-row>
            </template>
          </div>
          <div v-if="fields.policyItems.show" class="group-item">
            <div class="d-flex justify-end mb-2">
              <v-btn small color="primary" @click="handleAddPolicyItem">
                <v-icon size="16">mdi-plus</v-icon>
                <span>Add Item</span>
              </v-btn>
            </div>
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.policyItems.items" :key="`policy-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <span>{{ item.text || `Policy ${index + 1}` }}</span>
                    <v-btn
                      icon
                      :disabled="fields.policyItems.items.length <= 1"
                      @click.stop="handleRemovePolicyItem(index)"
                    >
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="mb-3">
                    <label>Icon</label>
                    <UploadImage
                      :image="{ imageSrc: item.icon }"
                      :height="`80`"
                      :typeUpload="'image'"
                      @callback="obj => (item.icon = obj.imageSrc)"
                      @callbackDelete="item.icon = ''"
                    />
                  </div>
                  <div>
                    <label>Text</label>
                    <v-text-field v-model="item.text" hide-details class="mt-0" />
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Description</label>
          <div class="group-item">
            <v-checkbox v-model="fields.description.show" label="Show description" hide-details class="mt-0 mb-2" />
            <ckeditor
              v-if="fields.description.show"
              :editor="editor"
              v-model="fields.description.content"
              :config="editorConfig"
            />
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">CTA Button</label>
          <div class="group-item">
            <ButtonPrimary :button="fields.button" :show-policy="false" />
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Media Image</label>
          <div class="group-item">
            <UploadImage
              :image="{ imageSrc: fields.media.image }"
              :height="`300`"
              :typeUpload="'image'"
              @callback="obj => (fields.media.image = obj.imageSrc)"
              @callbackDelete="fields.media.image = ''"
            />
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '../common/form/upload-file/upload-image.vue';
import ButtonPrimary from '../common/ui-modules/button-primary/editor.vue';
import SectionLayout from '../common/ui-modules/section-layout/editor.vue';
import ColorPicker from '../common/ui-modules/inputs/color-picker.vue';

const defaultPolicyIcon =
  'https://cdn.skyfastimg.com/6a0ee559dc2c2e000934dd37/2026/06/23/image-6a3a31ba055ca064b1125668.png';

const buildButtonDefault = () => ({
  align: 'flex-start',
  backgroundColor: '#006196',
  color: '#FFFFFF',
  radius: 12,
  iconPrefix: { show: false, src: '' },
  iconSuffix: { show: false, src: '' },
  text: 'Purchase Now',
  link: '#',
  show: true,
  subBtn: { show: false, text: '' },
  policy: { show: false, colPerView: 2, alignContent: 'center', items: [] },
});

const buildPolicyItemDefault = () => ({
  icon: defaultPolicyIcon,
  text: 'Try Risk Free | 30-Day Wear Test Guarantee',
});

const getDefaultFields = () => ({
  title: 'Image With Text Conven Feet 02 Section',
  settings: {
    paddingTop: true,
    paddingBottom: true,
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    overlayColor: '#000000',
    overlayOpacity: 0,
    showOverlay: false,
    backgroundImage: '',
    useCustomWidth: false,
    customWidth: 1204,
  },
  containerPage: {
    enableStyle: false,
    background: '#FFFFFF',
  },
  layout: {
    centerY: true,
    gapItem: 60,
    contentOrder: 1,
  },
  rateCard: {
    show: true,
    stars: 4,
    text: '14,000+ Happy Customers',
  },
  titleBlock: {
    show: true,
    content: "<h2>You'll Feel the Difference Instantly!</h2>",
  },
  policyItems: {
    show: true,
    color: '#006196',
    background: '#F2F7F9',
    items: [buildPolicyItemDefault(), buildPolicyItemDefault()],
  },
  description: {
    show: true,
    content:
      "<p>Our orthopedic shoes are lightweight, comfy, and built for all-day support. With great cushioning and slip-resistant soles, they're perfect for long shifts on any surface.</p><p>Whether you're in healthcare, retail, or hospitality, they'll keep your feet, legs, and back feeling good all day.</p>",
  },
  button: buildButtonDefault(),
  media: {
    image: 'https://cdn.skyfastimg.com/6a0ee559dc2c2e000934dd37/2026/06/23/image-6a3a2f8c7fecf41de2c93be4.png',
  },
});

export default {
  name: 'ImageWithTextConvenFeet02SectionEditor',
  components: { UploadImage, ButtonPrimary, SectionLayout, ColorPicker },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: getDefaultFields(),
    };
  },
  mounted() {
    const def = getDefaultFields();
    const extract = cloneDeep(this.sectionEditor.sectionExtract || {});

    this.fields = {
      ...def,
      ...extract,
      settings: { ...def.settings, ...(extract.settings || {}) },
      containerPage: { ...def.containerPage, ...(extract.containerPage || {}) },
      layout: { ...def.layout, ...(extract.layout || {}) },
      rateCard: { ...def.rateCard, ...(extract.rateCard || {}) },
      titleBlock: { ...def.titleBlock, ...(extract.titleBlock || {}) },
      policyItems: {
        ...def.policyItems,
        ...(extract.policyItems || {}),
        items:
          Array.isArray(extract.policyItems?.items) && extract.policyItems.items.length
            ? extract.policyItems.items
            : def.policyItems.items,
      },
      description: { ...def.description, ...(extract.description || {}) },
      button: { ...def.button, ...(extract.button || {}) },
      media: { ...def.media, ...(extract.media || {}) },
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionImageWithTextConvenFeet02(
        this.sectionEditor.section.html,
        this.fields,
      );
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddPolicyItem() {
      this.fields.policyItems.items.push(cloneDeep(buildPolicyItemDefault()));
    },
    handleRemovePolicyItem(index) {
      if (this.fields.policyItems.items.length <= 1) return;
      this.fields.policyItems.items.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.group-item {
  border: 1px solid #0000000f;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.ck-editor__editable_inline,
.ck-editor__editable {
  min-height: 140px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
